import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  CardGiftcard,
  Assessment,
  Settings,
  Notifications,
  AccountCircle,
  Logout,
  ChevronLeft,
  ChevronRight,
  AdminPanelSettings,
  Group,
  MonetizationOn,
  Security,
  Analytics,
  Support,
  BugReport,
  SportsEsports,
  Token,
  AccountBalance,
  SwapHoriz,
  SupervisorAccount,
  Chat,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
// import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/admin',
      badge: null,
    },
    {
      text: 'Users',
      icon: <People />,
      path: '/admin/users',
      badge: null,
    },
    {
      text: 'Agents',
      icon: <Business />,
      path: '/admin/agents',
      badge: null,
    },
    {
      text: 'Benefits',
      icon: <CardGiftcard />,
      path: '/admin/benefits',
      badge: null,
    },
    {
      text: 'Giveaways',
      icon: <MonetizationOn />,
      path: '/admin/giveaways',
      badge: null,
    },
    {
      text: 'Games',
      icon: <SportsEsports />,
      path: '/admin/games',
      badge: null,
    },
    {
      text: 'Tokens',
      icon: <Token />,
      path: '/admin/tokens',
      badge: null,
    },
    {
      text: 'Subscription Tokens',
      icon: <MonetizationOn />,
      path: '/admin/subscription-tokens',
      badge: null,
    },
    {
      text: 'Plan Management',
      icon: <AccountBalance />,
      path: '/admin/plans',
      badge: null,
    },
    {
      text: 'Token Allocation',
      icon: <SwapHoriz />,
      path: '/admin/token-allocation',
      badge: null,
    },
    {
      text: 'Redemptions',
      icon: <Assessment />,
      path: '/admin/redemptions',
      badge: null,
    },
    {
      text: 'Redemptions Chat',
      icon: <Chat />,
      path: '/admin/redemptions-chat',
      badge: null,
    },
    {
      text: 'Super Admin',
      icon: <SupervisorAccount />,
      path: '/admin/super-admin',
      badge: null,
    },
    {
      text: 'Analytics',
      icon: <Analytics />,
      path: '/admin/analytics',
      badge: null,
    },
    {
      text: 'Connections',
      icon: <Security />,
      path: '/admin/connections',
      badge: null,
    },
    {
      text: 'Reports',
      icon: <Assessment />,
      path: '/admin/reports',
      badge: null,
    },
    {
      text: 'Support',
      icon: <Support />,
      path: '/admin/support',
      badge: null,
    },
    {
      text: 'System Agents',
      icon: <BugReport />,
      path: '/admin/system-agents',
      badge: null,
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/admin/settings',
      badge: null,
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    handleProfileMenuClose();
  };

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#0A0A0A' }}>
      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: '#FFD700',
                color: '#0A0A0A',
                fontWeight: 'bold',
                width: 40,
                height: 40,
              }}
            >
              PV
            </Avatar>
            {!collapsed && (
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Plug Vault
                </Typography>
                <Typography variant="caption" sx={{ color: '#FFD700' }}>
                  Admin Panel
                </Typography>
              </Box>
            )}
          </Box>
        </div>
        
        {!isMobile && (
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{ color: 'white' }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Box>

      {/* Navigation Menu */}
      <List sx={{ pt: 2 }}>
        {menuItems.map((item, index) => (
          <div key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 215, 0, 0.1)',
                    borderLeft: '3px solid #FFD700',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.15)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#FFD700' : '#888',
                    minWidth: collapsed ? 'auto' : 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: location.pathname === item.path ? 'white' : '#CCC',
                        fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                      },
                    }}
                  />
                )}
                {!collapsed && item.badge && (
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: '#FF6B6B',
                        color: 'white',
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </div>
        ))}
      </List>

      {/* Admin Status */}
      {!collapsed && (
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AdminPanelSettings sx={{ color: '#FFD700', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
              {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </Typography>
          </Box>
          <Chip
            label="Online"
            size="small"
            sx={{
              bgcolor: '#4CAF50',
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#0A0A0A', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${collapsed ? 80 : drawerWidth}px)` },
          ml: { md: `${collapsed ? 80 : drawerWidth}px` },
          bgcolor: '#1A1A1A',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: 'white' }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Notifications */}
            <IconButton color="inherit">
              <Badge badgeContent={4} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            {/* Profile Menu */}
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ color: 'white' }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#FFD700', color: '#0A0A0A' }}>
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'A'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            bgcolor: '#1A1A1A',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <AccountCircle sx={{ color: '#FFD700' }} />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <Settings sx={{ color: '#FFD700' }} />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout sx={{ color: '#FF6B6B' }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: collapsed ? 80 : drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#0A0A0A',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? 80 : drawerWidth,
              bgcolor: '#0A0A0A',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${collapsed ? 80 : drawerWidth}px)` },
          mt: '64px',
        }}
      >
        <div>
          {children}
        </div>
      </Box>
    </Box>
  );
};

export default AdminLayout; 